import { Edit, Visibility, VisibilityOff } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
} from "amazon-cognito-identity-js";
import { Buffer } from "buffer";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import landingImage from "shared/assets/landing.webp";
import { useSnackbar } from "shared/common/contexts/SnackbarProvider";
import ClientConfig from "shared/config";
import { doWakeupRequest } from "shared/common/provider/pilegateprovider";
import {
  isLoggedin,
  redirectToExternalIDP,
  redirectToShibbolethIDP,
  tokenTypeWithProvider,
} from "shared/common/services/Login";
import { Infra3dLogo } from "shared/common/utils/icons/icons";

import logo from "shared/assets/infra3D_Logo_horizontal.png";
import { getApiKeyHeaders } from "shared/common/utils/general-utils";

interface FormData {
  email: string;
  password: string;
  newPassword: string;
  confirmPassword: string;
  verificationCode: number;
}

export type PoolMetaResponseType = Array<{
  poolId: string;
  tenantLabel: string;
  domainUrl: string;
  clients: Array<PoolMetaClientType>;
}>;

export type PoolMetaClientType = {
  clientId: string;
  clientType: "cognito" | "external";
  clientLabel: string;
};

export default function MultiTenantLogin({
  onLogin,
  tenant,
  mode,
}: {
  onLogin?: (tokens: tokenTypeWithProvider) => void;
  tenant?: string;
  mode?: "popup" | "redirect";
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showMessage, showUnhandledError } = useSnackbar();

  const [step, setStep] = useState<number>(0);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [, setResettingPassword] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [emailFieldDisabled, setEmailFieldDisabled] = useState<boolean>(false);
  const [poolMeta, setPoolMeta] = useState<PoolMetaResponseType | null>(null);
  const [cognitoUserState, setCognitoUserState] = useState<CognitoUser | null>(
    null
  );
  const [showSupportClient, setShowSupportClient] = useState<boolean>(false);

  const {
    register,
    resetField,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<FormData>();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.altKey && e.key === "s") {
        setShowSupportClient(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (isLoggedin()) {
      navigate("/");
    }

    if (tenant) {
      // redirect to idp if only one external client in poolMeta
      const redirectWhenOnlyOneExternalClient = async (
        poolMeta: PoolMetaResponseType
      ) => {
        if (!poolMeta) return;
        if (
          getCognitoClients(poolMeta[0].clients).length === 0 &&
          getExternalClients(poolMeta[0].clients).length === 1
        ) {
          try {
            const credentials = await redirectToExternalIDP(
              poolMeta[0].domainUrl,
              getExternalClients(poolMeta[0].clients)[0].clientId,
              mode
            );
            if (onLogin) {
              onLogin(credentials);
            }
          } catch (err) {
            if (err instanceof Error && err.message === "Popup blocked") {
              showMessage(t("alert_error.blocked_popup"), "error");
            } else {
              showUnhandledError(String(err));
            }
          }
        }
      };

      fetchLoginDataByTenant(tenant)
        .then((res) => {
          setPoolMeta(res);
          redirectWhenOnlyOneExternalClient(res);
          setStep(1);
        })
        .catch((err) => {
          // show snackbar message when tenant couldn't be resolved
          showMessage(t("login.alert_tenant_not_found"), "warning");
          // redirect when tenantname couldn't be resolved
          navigate("/login");
        });
    }
  }, []);

  const createCognitoIdentity = useCallback(() => {
    if (!poolMeta) return null;

    // Create user pool object
    const userPool = new CognitoUserPool({
      UserPoolId: poolMeta[0].poolId,
      ClientId: getCognitoClients(poolMeta[0].clients)[0].clientId,
    });

    // Create Cognito user object
    const cognitoUser = new CognitoUser({
      Username: getValues("email"),
      Pool: userPool,
    });

    if (!onLogin) {
      localStorage.setItem(
        "cognitoInfos",
        JSON.stringify({
          domain: poolMeta[0].domainUrl,
          clientId: getCognitoClients(poolMeta[0].clients)[0].clientId,
        })
      );
    }

    return cognitoUser;
  }, [poolMeta, getValues]);

  const getCognitoClients = (clients: Array<PoolMetaClientType>) => {
    return clients.filter((client) => client.clientType === "cognito");
  };

  const getExternalClients = (clients: Array<PoolMetaClientType>) => {
    return clients.filter(
      (client) =>
        client.clientType === "external" &&
        client.clientLabel !== "inovitas-support-admin"
    );
  };

  const getSupportClient = (clients: Array<PoolMetaClientType>) => {
    return clients.filter(
      (client) =>
        client.clientType === "external" &&
        client.clientLabel === "inovitas-support-admin"
    );
  };

  const fetchLoginDataByTenant = async (
    tenant_name: string
  ): Promise<PoolMetaResponseType> => {
    const tennantBase64 = Buffer.from(tenant_name).toString("base64");
    return fetch(`${ClientConfig.loginURL}poolMetas/tenant/${tennantBase64}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...getApiKeyHeaders(),
      },
    })
      .then(async (response) => {
        const json = await response.json();
        if (json.length === 0) {
          throw new Error("Tenant not found in DynamoDB");
        }
        return json;
      })
      .catch((error) => {
        throw error;
      });
  };

  const fetchLoginDataByEmail = async (
    user_email: string
  ): Promise<PoolMetaResponseType> => {
    const emailBase64 = Buffer.from(user_email).toString("base64");
    return fetch(`${ClientConfig.loginURL}poolMetas/user/${emailBase64}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...getApiKeyHeaders(),
      },
    })
      .then(async (response) => {
        const json = await response.json();
        if (json.length === 0) {
          throw new Error("User not found in DynamoDB");
        }
        return json;
      })
      .catch((error) => {
        throw error;
      });
  };

  // Function to submit email and handle API response
  const submitEmail = async (data: FormData) => {
    setLoading(true);
    fetchLoginDataByEmail(data.email)
      .then((res) => {
        setEmailFieldDisabled(true);
        setPoolMeta(res);
        setStep(1);
        doWakeupRequest(); // wake up pilegate lambda
      })
      .catch((err) => {
        // show snackbar message when email couldn't be resolved
        showMessage(t("login.alert_user_not_found"), "warning");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Function to submit login and handle authentication
  const submitLogin = async (data: FormData) => {
    if (!poolMeta) return;
    setLoading(true);
    const cognitoUser = createCognitoIdentity();
    if (!cognitoUser) return;
    try {
      // Prepare authentication data
      const authenticationData = {
        Username: data.email,
        Password: data.password,
      };

      // Create authentication details object
      const authenticationDetails = new AuthenticationDetails(
        authenticationData
      );

      // Authenticate user
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: async function (session) {
          // Extract tokens
          if (!onLogin) {
            const tokens = {
              id_token: session.getIdToken().getJwtToken(),
              access_token: session.getAccessToken().getJwtToken(),
              refresh_token: session.getRefreshToken().getToken(),
            };
            localStorage.setItem("tokens", JSON.stringify(tokens));
            navigate("/");
            setLoading(false);
          } else {
            const tokens: tokenTypeWithProvider = {
              domain: poolMeta[0].domainUrl,
              clientId: getCognitoClients(poolMeta[0].clients)[0].clientId,
              token_type: "Bearer",
              id_token: session.getIdToken().getJwtToken(),
              access_token: session.getAccessToken().getJwtToken(),
              refresh_token: session.getRefreshToken().getToken(),
              expires_in:
                session.getAccessToken().getExpiration() -
                Math.floor(Date.now() / 1000),
            };
            onLogin(tokens);
          }
        },
        onFailure: function (err) {
          // Check if the error is due to unauthorized user
          if (err.name === "NotAuthorizedException") {
            showMessage(t("login.alert_incorrect_password"), "warning");
          }
          // Check if the error is due to expired access
          else if (
            err.code === "UserLambdaValidationException" &&
            err.message.includes("access expired")
          ) {
            const match = err.message.match(
              /access expired on (\d{4}-\d{2}-\d{2})/
            );
            if (match) {
              let expirationDate = match[1];
              const [year, month, day] = expirationDate.split("-");
              expirationDate = `${day}.${month}.${year}`;
              showMessage(
                <>
                  {t("login.alert_account_expired_1").replace(
                    "<date>",
                    expirationDate
                  )}
                  <Link
                    href="mailto:sales@inovitas.ch"
                    sx={{
                      color: "primary.contrastText",
                      textDecoration: "underline",
                    }}
                  >
                    {t("general.email_sales")}
                  </Link>
                  {t("login.alert_account_expired_2")}
                </>,
                "warning"
              );
            }
          } else {
            showUnhandledError(String(err));
          }
          setLoading(false);
        },
        newPasswordRequired: function (userAttributes, requiredAttributes) {
          // Set resettingPassword state
          setResettingPassword(true);
          setLoading(false);
          resetField("password");
          // Set as stage to use authenticated user in next step
          setCognitoUserState(cognitoUser);
          setStep(2);
        },
      });
    } catch (err) {
      showUnhandledError(String(err));
      setLoading(false);
    }
  };

  // Function to handle initial password change
  const setNewPassword = async (data: FormData) => {
    if (!poolMeta) return;
    setLoading(true);
    // Get authenticated user from state
    const cognitoUser = cognitoUserState;
    if (!cognitoUser) return;
    try {
      // Get new password from form data
      const newPassword = data.newPassword;

      // Complete new password challenge
      cognitoUser.completeNewPasswordChallenge(
        newPassword,
        {},
        {
          onSuccess: function (session, userConfirmationNecessary) {
            // Handle successful password change
            setResettingPassword(false);
            showMessage(t("login.alert_set_new_password"), "success");
            resetField("newPassword");
            resetField("confirmPassword");
            setStep(1);
            setLoading(false);
          },
          onFailure: function (err) {
            if (err.name === "InvalidPasswordException") {
              showMessage(t("login.alert_password_not_conform"), "warning");
            } else {
              showUnhandledError(String(err));
            }
            setLoading(false);
          },
        }
      );
    } catch (error) {
      showUnhandledError(String(error));
      setLoading(false);
    }
  };

  // Function for sending the verification code
  const sendVerificationCode = async () => {
    setLoading(true);
    const cognitoUser = createCognitoIdentity();
    if (!cognitoUser) return;
    try {
      cognitoUser.forgotPassword({
        onSuccess: function (data) {
          showMessage(t("login.alert_forgot_password"));
          setLoading(false);
        },
        onFailure: function (err) {
          showUnhandledError(String(err));
          setLoading(false);
        },
      });
    } catch (err) {
      showUnhandledError(String(err));
      setLoading(false);
    }
  };

  // Function for resetting the password
  const resetPassword = async (data: FormData) => {
    setLoading(true);
    const cognitoUser = createCognitoIdentity();
    if (!cognitoUser) return;
    try {
      // Get new password from form data
      const newPassword = data.newPassword;
      const verificationCode = data.verificationCode.toString();

      // Complete new password challenge
      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess: function () {
          // Successfully change the password
          setResettingPassword(false);
          showMessage(t("login.alert_set_new_password"), "success");
          resetField("newPassword");
          resetField("confirmPassword");
          setStep(1);
          setLoading(false);
        },
        onFailure: function (err) {
          if (
            err.name === "CodeMismatchException" ||
            err.name === "ExpiredCodeException"
          ) {
            showMessage(t("login.alert_bad_verification_code"), "warning");
          }
          // Error when changing the password
          else if (err.name === "InvalidPasswordException") {
            showMessage(t("login.alert_password_not_conform"), "warning");
          } else {
            showUnhandledError(String(err));
          }
          setLoading(false);
        },
      });
    } catch (err) {
      showUnhandledError(String(err));
      setLoading(false);
    }
  };

  // Function to edit email
  const editEmail = () => {
    setStep(0);
    resetField("password");
    setShowPassword(false);
  };

  // Function to toggle password visibility
  const displayPassword = () => {
    setShowPassword(!showPassword);
  };

  // Function to toggle new password visibility
  const displayNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  // Function to toggle confirm password visibility
  const displayConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Function to render the email section
  function renderEmailSection() {
    return (
      <form onSubmit={handleSubmit(submitEmail)}>
        <Box display="flex" flexDirection="column" gap="32px">
          <TextField
            autoFocus
            error={errors.email !== undefined ? true : false}
            fullWidth
            helperText={errors.email ? errors.email.message : ""}
            label={t("login.email_label")}
            type="email"
            variant="outlined"
            {...register("email", {
              required: (t("login.email_error_message") as string) || "",
              pattern:
                /^[A-Za-z0-9-!#$%&'*+-/=?^_`{|}~]+@[a-z0-9._-]+\.[A-Za-z]{2,6}$/,
            })}
          />
          <LoadingButton
            fullWidth
            loading={loading}
            size="large"
            type="submit"
            variant="contained"
          >
            {t("login.continue")}
          </LoadingButton>
          <Divider orientation="horizontal">
            <Typography variant="subtitle2">
              {t("login.or").toUpperCase()}
            </Typography>
          </Divider>
          <LoadingButton
            fullWidth
            loading={loading}
            onClick={async () => {
              try {
                setLoading(true);
                const credentials = await redirectToShibbolethIDP(mode);
                if (onLogin) {
                  onLogin(credentials);
                }
              } catch (err) {
                if (err instanceof Error && err.message === "Popup blocked") {
                  showMessage(t("alert_error.blocked_popup"), "error");
                } else {
                  showUnhandledError(String(err));
                }
              } finally {
                setLoading(false);
              }
            }}
            size="large"
            variant="outlined"
          >
            <Infra3dLogo sx={{ paddingRight: 2 }} />
            {t("login.continue_classic")}
          </LoadingButton>
        </Box>
      </form>
    );
  }

  // Function to render the email/password section
  function renderEmailPasswordSection() {
    return (
      <form onSubmit={handleSubmit(submitLogin)} style={{ flexGrow: 1 }}>
        <Box display="flex" flexDirection="column" gap="32px">
          {poolMeta && getCognitoClients(poolMeta[0].clients).length > 0 && (
            <>
              <Box display="flex" flexDirection="column" gap="16px">
                <TextField
                  autoFocus={!emailFieldDisabled}
                  disabled={emailFieldDisabled}
                  error={errors.email !== undefined ? true : false}
                  fullWidth
                  helperText={errors.email ? errors.email.message : ""}
                  label={t("login.email_label")}
                  type="email"
                  variant="outlined"
                  {...register("email", {
                    required: (t("login.email_error_message") as string) || "",
                    pattern:
                      /^[A-Za-z0-9-!#$%&'*+-/=?^_`{|}~]+@[a-z0-9._-]+\.[A-Za-z]{2,6}$/,
                  })}
                  InputProps={{
                    endAdornment: emailFieldDisabled && (
                      <IconButton
                        aria-label="edit email"
                        onClick={editEmail}
                        edge="end"
                        disableRipple
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    ),
                  }}
                />
                <TextField
                  autoFocus={emailFieldDisabled}
                  error={errors.password !== undefined ? true : false}
                  fullWidth
                  helperText={errors.password ? errors.password.message : ""}
                  label={t("login.password_label")}
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  {...register("password", {
                    required:
                      (t("login.password_error_message") as string) || "",
                  })}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={displayPassword}
                        edge="end"
                        disableRipple
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                  }}
                />
                <Box display={"flex"} justifyContent={"end"}>
                  <Link
                    component="button"
                    sx={{ marginTop: "-8px" }}
                    type="button"
                    underline="hover"
                    variant="body2"
                    onClick={(e) => {
                      e.preventDefault();
                      setStep(3);
                    }}
                  >
                    {t("login.forgot_password")}
                  </Link>
                </Box>
              </Box>
              <LoadingButton
                fullWidth
                loading={loading}
                size="large"
                type="submit"
                variant="contained"
              >
                {t("login.sign_in")}
              </LoadingButton>
            </>
          )}
          {poolMeta && getExternalClients(poolMeta[0].clients).length > 0 && (
            <>
              {poolMeta &&
                getCognitoClients(poolMeta[0].clients).length > 0 && (
                  <Divider orientation="horizontal">
                    <Typography variant="subtitle2">
                      {t("login.or").toUpperCase()}
                    </Typography>
                  </Divider>
                )}
              <div
                style={{
                  display: "grid",
                  gap: "16px",
                }}
              >
                {getExternalClients(poolMeta[0].clients).map((idp, index) => (
                  <LoadingButton
                    fullWidth
                    loading={loading}
                    key={index}
                    size="large"
                    variant="outlined"
                    onClick={async () => {
                      try {
                        setLoading(true);
                        const credentials = await redirectToExternalIDP(
                          poolMeta[0].domainUrl,
                          idp.clientId,
                          mode
                        );
                        if (onLogin) {
                          onLogin(credentials);
                        }
                      } catch (err) {
                        if (
                          err instanceof Error &&
                          err.message === "Popup blocked"
                        ) {
                          showMessage(t("alert_error.blocked_popup"), "error");
                        } else {
                          showUnhandledError(String(err));
                        }
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    {idp.clientLabel}
                  </LoadingButton>
                ))}
              </div>
            </>
          )}
          {showSupportClient &&
            poolMeta &&
            getSupportClient(poolMeta[0].clients).length > 0 && (
              <>
                {getSupportClient(poolMeta[0].clients).map((idp, index) => (
                  <LoadingButton
                    fullWidth
                    loading={loading}
                    key={index}
                    size="large"
                    variant="outlined"
                    onClick={async () => {
                      try {
                        const credentials = await redirectToExternalIDP(
                          poolMeta[0].domainUrl,
                          idp.clientId,
                          mode
                        );
                        if (onLogin) {
                          onLogin(credentials);
                        }
                      } catch (err) {
                        if (
                          err instanceof Error &&
                          err.message === "Popup blocked"
                        ) {
                          showMessage(t("alert_error.blocked_popup"), "error");
                        } else {
                          showUnhandledError(String(err));
                        }
                      }
                    }}
                  >
                    {idp.clientLabel}
                  </LoadingButton>
                ))}
              </>
            )}
        </Box>
      </form>
    );
  }

  // Function to render the force password section
  function renderForceChangePasswordSection() {
    return (
      <form onSubmit={handleSubmit(setNewPassword)}>
        <Box display="flex" flexDirection="column" gap="32px">
          <Box display="flex" flexDirection="column" gap="16px">
            <TextField
              error={!!errors.newPassword}
              fullWidth
              helperText={errors.newPassword ? errors.newPassword.message : ""}
              label={t("login.new_password")}
              type={showNewPassword ? "text" : "password"}
              variant="outlined"
              {...register("newPassword", {
                required: (t("login.password_error_message") as string) || "",
                pattern: {
                  value:
                    /^(?=.*[0-9])(?=.*[!@#$%^&*()\-_+={}[\]|\\:;"'<>,.?/])(?=.*[A-Z])(?=.*[a-z]).{8,}$/,
                  message:
                    (t("login.alert_password_not_conform") as string) || "",
                },
              })}
              InputProps={{
                endAdornment: (
                  <IconButton
                    aria-label="toggle new password visibility"
                    onClick={displayNewPassword}
                    edge="end"
                    disableRipple
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
            <TextField
              error={errors.confirmPassword !== undefined ? true : false}
              fullWidth
              helperText={
                errors.confirmPassword ? errors.confirmPassword.message : ""
              }
              label={t("login.confirm_password")}
              type={showConfirmPassword ? "text" : "password"}
              variant="outlined"
              {...register("confirmPassword", {
                required: (t("login.password_mismatch") as string) || "",
                validate: (value: string) =>
                  value === getValues("newPassword") ||
                  (t("login.password_mismatch") as string),
              })}
              InputProps={{
                endAdornment: (
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={displayConfirmPassword}
                    edge="end"
                    disableRipple
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
          </Box>
          <LoadingButton
            fullWidth
            loading={loading}
            size="large"
            type="submit"
            variant="contained"
          >
            {t("login.confirm")}
          </LoadingButton>
        </Box>
      </form>
    );
  }

  // Function to render the forgot password section
  function renderForgotPasswordSection() {
    return (
      <form
        onSubmit={handleSubmit(() => {
          sendVerificationCode();
          setStep(4);
        })}
      >
        <Box display="flex" flexDirection="column" gap="32px">
          <Box display="flex" flexDirection="column" gap="16px">
            <TextField
              autoFocus
              error={errors.email !== undefined ? true : false}
              fullWidth
              helperText={errors.email ? errors.email.message : ""}
              label={t("login.email_label")}
              type="email"
              variant="outlined"
              {...register("email", {
                required: (t("login.email_error_message") as string) || "",
                pattern:
                  /^[A-Za-z0-9-!#$%&'*+-/=?^_`{|}~]+@[a-z0-9._-]+\.[A-Za-z]{2,6}$/,
              })}
            />
          </Box>
          <LoadingButton
            fullWidth
            loading={loading}
            size="large"
            type="submit"
            variant="contained"
          >
            {t("login.continue")}
          </LoadingButton>
        </Box>
      </form>
    );
  }

  // Function to render the forgot password verify section
  function renderForgotPasswordVerifySection() {
    return (
      <form onSubmit={handleSubmit(resetPassword)}>
        <Box display="flex" flexDirection="column" gap="32px">
          <Box display="flex" flexDirection="column" gap="16px">
            <TextField
              autoFocus={true}
              error={!!errors.verificationCode}
              fullWidth
              helperText={
                errors.verificationCode ? errors.verificationCode.message : ""
              }
              label={t("login.verification_code")}
              type="number"
              variant="outlined"
              {...register("verificationCode", {
                required:
                  (t("login.verification_code_error_message") as string) || "",
              })}
            />
            <TextField
              error={!!errors.newPassword}
              fullWidth
              helperText={errors.newPassword ? errors.newPassword.message : ""}
              label={t("login.new_password")}
              type={showNewPassword ? "text" : "password"}
              variant="outlined"
              {...register("newPassword", {
                required: (t("login.password_error_message") as string) || "",
                pattern: {
                  value:
                    /^(?=.*[0-9])(?=.*[!@#$%^&*()\-_+={}[\]|\\:;"'<>,.?/])(?=.*[A-Z])(?=.*[a-z]).{8,}$/,
                  message:
                    (t("login.alert_password_not_conform") as string) || "",
                },
              })}
              InputProps={{
                endAdornment: (
                  <IconButton
                    aria-label="toggle new password visibility"
                    disableRipple
                    edge="end"
                    onClick={displayNewPassword}
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
            <TextField
              error={errors.confirmPassword !== undefined ? true : false}
              fullWidth
              helperText={
                errors.confirmPassword ? errors.confirmPassword.message : ""
              }
              label={t("login.confirm_password")}
              type={showConfirmPassword ? "text" : "password"}
              variant="outlined"
              {...register("confirmPassword", {
                required: (t("login.password_mismatch") as string) || "",
                validate: (value: string) =>
                  value === getValues("newPassword") ||
                  (t("login.password_mismatch") as string),
              })}
              InputProps={{
                endAdornment: (
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={displayPassword}
                    edge="end"
                    disableRipple
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
          </Box>
          <LoadingButton
            fullWidth
            loading={loading}
            size="large"
            type="submit"
            variant="contained"
          >
            {t("login.confirm")}
          </LoadingButton>
          <Box display="flex" justifyContent="center" gap="4px">
            <Typography variant="body2" align="center">
              {t("login.verification_code_not_received")}
            </Typography>
            <Link
              component="button"
              onClick={sendVerificationCode}
              type="button"
              underline="hover"
              variant="body2"
            >
              {t("login.verification_code_resend")}
            </Link>
          </Box>
        </Box>
      </form>
    );
  }

  return (
    <>
      {isLoggedin() ? (
        <></>
      ) : (
        <>
          <Box
            alignItems="center"
            display="flex"
            justifyContent="center"
            minHeight="100%"
            sx={{
              backgroundImage: `url(${landingImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              position: "relative",
              "&::before": {
                // Use ::before pseudo-element for the blur
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backdropFilter: "blur(10px)",
              },
            }}
          >
            <Box
              gap="32px"
              maxWidth="400px"
              minHeight={step in [2, 3] ? "480px" : "100%"}
              paddingX="64px"
              paddingY="48px"
              sx={{
                backgroundColor: "white",
                borderRadius: "10px",
                position: "relative",
                display: "flex",
                flexDirection: "column",
              }}
              width="100%"
            >
              <Box display="flex" flexDirection="column" gap="16px">
                <Typography variant="h4">{t("login.welcome_title")}</Typography>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  paddingBottom="32px"
                >
                  <img
                    style={{ width: "250px" }}
                    src={logo}
                    alt="infra3D Logo"
                  />
                </Box>
              </Box>
              {step === 0 && renderEmailSection()}
              {step === 1 && renderEmailPasswordSection()}
              {step === 2 && renderForceChangePasswordSection()}
              {step === 3 && renderForgotPasswordSection()}
              {step === 4 && renderForgotPasswordVerifySection()}
              <Box display="flex" justifyContent="end" gap="16px">
                <Link
                  type="button"
                  variant="body2"
                  href="https://help.infra3d.com/privacy"
                  target="_blank"
                  underline="hover"
                >
                  {t("login.privacy_policy")}
                </Link>
                <Link
                  type="button"
                  variant="body2"
                  href="https://infra3d.com/agb/"
                  target="_blank"
                  underline="hover"
                >
                  {t("login.agb")}
                </Link>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </>
  );
}
